import React, { useState, useEffect } from "react";
import { Box, Chip, Grid, MenuItem, OutlinedInput, Select, Typography } from "@mui/material";
import { TextInputComponent } from "../../../../component/atom";
import TextAreaComponent from "../../../../component/atom/Inputs/TextArea";
import ThumbnailUploadComponent from "../../../../component/atom/Inputs/Thumbnail";
import { getText, renderTruncatedText } from "../../../../core/Constant";
import FolderTreeNode from "../../../molecule/FolderTreeSelection";
import { useCourseFolderSelection } from "../../../../hook/useCourseFolderSelection";

const QuizFormBody = ({ form, folderItems, loadingFolders, courseList = [], isViewOnly = false, nameApiError = "", setNameApiError, thumbnailApiError = "" }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleScroll = (event) => {
      if (event.target.classList && event.target.classList.contains("MuiMenu-list")) {
        return;
      }
      if (open) {
        setOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  const MenuProps = {
    disableScrollLock: true,
    PaperProps: {
      style: {
        maxHeight: 48 * 4.5 + 8,
        width: 250,
      },
    },
  };

  // Helper to parse folders
  const getSelectedFolders = () => {
    const selected = [];
    const selectedIds = new Set((form.selectedFolderIds || []).map(String));
    const getNormalizedList = () => {
      if (!folderItems) return [];
      let target = folderItems?.data?.quiz_directories || folderItems.quiz_directories || folderItems;
      if (Array.isArray(target)) return target;
      return Object.values(target || {});
    };

    const traverse = (node) => {
      if (!node) return;
      if (selectedIds.has(String(node.id))) selected.push(node);
      const childNodes = Array.isArray(node.children) ? node.children : Object.values(node.children || {});
      childNodes.forEach(traverse);
    };

    getNormalizedList().forEach(traverse);
    return selected;
  };

  const selectedCourses = form.selectedCourses || [];
  const handleFolderToggle = (id) => form.toggleFolderSelection(id, folderItems);

  useCourseFolderSelection({
    folderItems,
    selectedCourses,
    courseList,
    selectedFolderIds: form.selectedFolderIds,
    latestSelectedCourseId: form.latestSelectedCourseId,
    syncCourseFolderSelections: form.syncCourseFolderSelections,
  });

  return (
    <Box sx={{ background: "#ffffff", minHeight: "100vh", mt: 2 }}>
      {/* STEP 1: BASIC DETAILS */}
      <Box sx={{ background: "#fff", borderRadius: "16px", p: 4, border: "1px solid #E7E7E7" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              bgcolor: "#F1E7FF",
              color: "#7B3FF2",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: 700,
              mr: 2,
            }}
          >
            1
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "24px" }}>Basic Details</Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {/* SELECT COURSES */}
              <Grid item xs={12}>
                <Typography
                  sx={{
                    borderRadius: "10px",
                    padding: 0,
                    margin: 0,
                    marginBottom: 1,
                    color: "#4E657C",
                    fontSize: 19,
                    fontWeight: 700,
                  }}
                >
                  Select Courses
                </Typography>
                <Select
                  multiple
                  disabled={isViewOnly}
                  value={selectedCourses}
                  onChange={(e) => {
                    form.handleCourseChange(e);
                  }}
                  input={<OutlinedInput placeholder="Select Courses" />}
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) {
                      return <span style={{ color: "#aaa" }}>Select Courses</span>;
                    }
                    const textString = selected
                      .map((id) => {
                        const course = courseList.find(
                          (c) => String(c.id) === String(id)
                        );
                        return course?.name;
                      })
                      .join(", ");

                    return renderTruncatedText(textString, 45);
                  }}
                  MenuProps={MenuProps}
                  style={{ width: "100%" }}
                  displayEmpty
                >
                  {courseList.map((course) => {
                    const cId = course.id;
                    const courseName = course.name || "";
                    return (
                      <MenuItem key={cId} value={cId}>
                        {renderTruncatedText(courseName, 50)}
                      </MenuItem>
                    );
                  })}
                </Select>

                {/* SELECTED COURSE CHIPS */}
                <Box display="flex" flexWrap="wrap" gap={1} mt={1.5}>
                  {selectedCourses.map((id) => {
                    const matchedCourse = courseList.find(
                      (course) => String(course.id) === String(id)
                    );
                    const courseName = matchedCourse?.name;
                    return (
                      <Chip
                        key={id}
                        label={renderTruncatedText(courseName, 25)}
                        onDelete={isViewOnly ? undefined : () => form.handleChipDelete(id, folderItems)}
                        variant="outlined"
                      />
                    );
                  })}
                </Box>
              </Grid>

              {/* QUIZ NAME */}
              <Grid item xs={12}>
                <TextInputComponent
                  label="Quiz Name"
                  value={form.name}
                  disabled={isViewOnly}
                  onchange={(e) => {
                    if (setNameApiError) setNameApiError(""); // Clear API error on typing
                    form.onChangeName(e);
                  }}
                  placeholder="e.g. ADA Policies Final Quiz"
                  isRequired={!isViewOnly}
                  isError={form.isNameError || Boolean(nameApiError)}
                  error={nameApiError || "Add Valid Quiz Name"}
                />
              </Grid>

              {/* ATTEMPTS */}
              <Grid item xs={12}>
                <TextInputComponent
                  label="No. of Attempts"
                  placeholder="Enter number of attempts"
                  value={form.noAttempt}
                  disabled={isViewOnly}
                  type="number"
                  onchange={form.onChangeAttempt}
                  isRequired={!isViewOnly}
                  isError={form.isNoAttemptError}
                  error="Add Valid Attempts"
                />
              </Grid>

              {/* TIME DURATION */}
              <Grid item xs={12}>
                <TextInputComponent
                  label="Time Duration (Minutes)"
                  placeholder="Enter time duration"
                  value={form.time}
                  disabled={isViewOnly}
                  type="number"
                  onchange={form.onChangeTime}
                  isRequired={!isViewOnly}
                  isError={form.isTimeError}
                  error="Add Valid Duration"
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={3}>
              {/* DESCRIPTION */}
              <Grid item xs={12}>
                <TextAreaComponent
                  textlabel="Description"
                  value={form.description}
                  disabled={isViewOnly}
                  onchange={form.onChangeDescription}
                  placeholder="Briefly describe the purpose of this quiz..."
                  isRequired={!isViewOnly}
                  rows={5}
                  isError={form.isDescriptionError}
                  error="Add Valid Description"
                />
              </Grid>

              {/* THUMBNAIL */}
              <Grid item xs={12}>
                <ThumbnailUploadComponent
                  value={form.thumbnail}
                  disabled={isViewOnly}
                  onChange={(val) => {
                    if (isViewOnly) return;
                    form.setThumbnail(val);
                    if (!val && form.setThumbnailRemoved) {
                      form.setThumbnailRemoved(true);
                    }
                  }}
                  label="Quiz Thumbnail"
                  isError={Boolean(thumbnailApiError)}
                  error={thumbnailApiError}
                  description="PNG, JPG, JPEG up to 2MB"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* STEP 2: ASSIGN FOLDERS */}
      <Box sx={{ background: "#fff", borderRadius: "16px", p: 4, border: "1px solid #E7E7E7", mt: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              bgcolor: "#F1E7FF",
              color: "#7B3FF2",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontWeight: 700,
              mr: 2,
            }}
          >
            2
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: "24px" }}>Assign Quiz to Folder(s)</Typography>
        </Box>

        {!isViewOnly && (
          <>
            <Typography sx={{ color: "#667085", mb: 0.5 }}>Select one or more folders where this quiz should appear.</Typography>
            <Box mt={3}>
              <TextInputComponent
                placeholder="Search folder..."
                value={form.folderSearch}
                onchange={(e) => form.setFolderSearch(getText(e))}
                width="450px"
              />
            </Box>
          </>
        )}

        <Box sx={{ mt: 3 }}>
          <FolderTreeNode
            foldersData={folderItems}
            loading={loadingFolders}
            selectedFolderIds={form.selectedFolderIds}
            onFolderToggle={isViewOnly ? () => {} : handleFolderToggle}
            searchQuery={form.folderSearch}
            disabled={isViewOnly}
          />
        </Box>

        <Box mt={3}>
          <Typography fontWeight={600}>Selected Folder(s):</Typography>
          <Box sx={{ mt: 1, border: "1px dashed #E4E7EC", borderRadius: 2, p: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {getSelectedFolders().map((folder) => (
              <Chip
                key={folder.id}
                label={renderTruncatedText(folder.name, 25)}
                onDelete={isViewOnly ? undefined : () => handleFolderToggle(folder.id)}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default QuizFormBody;